// The forward test (admin only): the founder's real trades by MoneyHQ's tested rules, each paired
// with the paper trade for the same signal, so the record shows what the rules did with real money
// and whether paper fills, costs and results match real ones. Rules: docs/forward-test.md.
import { useEffect, useState } from 'react';
import { Link } from '@/lib/nav';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

interface Leg { entry: number; entryDate: string; exit?: number; exitDate?: string; costsPct: number; netPct?: number; status: string }
interface Pair { symbol: string; rule: 'breakout' | 'momentum'; real: Leg; paper: Leg; entryGapBps: number; exitGapBps?: number; netGapPoints?: number }
interface Report {
  pairs: Pair[]; offRule: { symbol: string; entryAt: string; entry: number }[]; missedReal: string[];
  summary: { paired: number; closedBoth: number; avgEntryGapBps: number; avgExitGapBps?: number; realAvgNetPct?: number; paperAvgNetPct?: number; realNetRupees: number; offRule: number };
}

const px = (v?: number) => (v == null ? '—' : v.toLocaleString('en-IN', { maximumFractionDigits: 2 }));
const pct = (v?: number) => (v == null ? '—' : `${v >= 0 ? '+' : '−'}${Math.abs(v).toFixed(2)}%`);
const bps = (v?: number) => (v == null ? '—' : `${v >= 0 ? '+' : '−'}${Math.abs(v).toFixed(0)} bp`);
const tone = (v?: number) => (v == null ? '' : v >= 0 ? 'text-bull' : 'text-bear');

export function ForwardTest() {
  const [r, setR] = useState<Report | null>(null);
  useEffect(() => { void api('/v1/forward-test').then((x) => (x.ok ? x.json() : null)).then(setR).catch(() => setR(null)); }, []);
  if (!r) return null;
  const s = r.summary;
  return (
    <Card className="mt-6 gap-0 py-0">
      <CardHeader className="border-b py-4">
        <CardTitle className="text-[15px] font-semibold">Forward test · real money vs paper <span className="ml-1 rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">admin only</span></CardTitle>
        <p className="mt-0.5 text-xs text-muted-foreground">
          Your real trades (Zerodha import) paired with the paper trade for the same signal: the new-high breakout or the momentum basket, entries within 3 days.
          The rules and sizing are fixed in docs/forward-test.md. A few trades show the system works with real money; the edge needs many more.
        </p>
      </CardHeader>
      <CardContent className="space-y-4 py-4 text-[13px]">
        <div className="flex flex-wrap gap-x-8 gap-y-2">
          {[
            ['Signals traded both ways', String(s.paired), `${s.closedBoth} closed`],
            ['Entry: real vs paper', bps(s.avgEntryGapBps), '+ = paid more than the paper fill'],
            ['Exit: real vs paper', bps(s.avgExitGapBps), '+ = sold higher'],
            ['Average result, closed', `${pct(s.realAvgNetPct)} real`, `${pct(s.paperAvgNetPct)} paper, after costs`],
            ['Real money, closed', `₹${px(s.realNetRupees)}`, 'paired trades, after charges'],
            ['Off the rules', String(s.offRule), 'real trades with no signal'],
          ].map(([k, v, sub]) => (
            <div key={k}><div className="text-xs text-muted-foreground">{k}</div><div className="num text-lg font-semibold">{v}</div><div className="text-[11px] text-muted-foreground">{sub}</div></div>
          ))}
        </div>
        {r.pairs.length === 0 ? (
          <p className="text-muted-foreground">No paired trades yet. Place a paper trade by the rule, buy the same signal in your Zerodha account, then import the tradebook in the Journal.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px]">
              <thead className="text-left text-[11px] tracking-wide text-muted-foreground uppercase">
                <tr className="border-b">{['Stock', 'Rule', 'Real entry', 'Paper entry', 'Gap', 'Real exit', 'Paper exit', 'Real', 'Paper'].map((h) => <th key={h} className="px-2 py-1.5 font-medium">{h}</th>)}</tr>
              </thead>
              <tbody>
                {r.pairs.map((p) => (
                  <tr key={p.symbol + p.real.entryDate} className="border-b border-border/60 last:border-b-0">
                    <td className="px-2 py-1.5 font-semibold"><Link href={`/stock/${p.symbol}`} className="hover:text-brand-strong">{p.symbol}</Link></td>
                    <td className="px-2 py-1.5 text-muted-foreground">{p.rule === 'breakout' ? 'New-high breakout' : 'Momentum basket'}</td>
                    <td className="num px-2 py-1.5">{px(p.real.entry)}<div className="text-[11px] text-muted-foreground">{p.real.entryDate}</div></td>
                    <td className="num px-2 py-1.5">{px(p.paper.entry)}<div className="text-[11px] text-muted-foreground">{p.paper.entryDate}</div></td>
                    <td className="num px-2 py-1.5">{bps(p.entryGapBps)}</td>
                    <td className="num px-2 py-1.5">{px(p.real.exit)}<div className="text-[11px] text-muted-foreground">{p.real.exitDate ?? p.real.status}</div></td>
                    <td className="num px-2 py-1.5">{px(p.paper.exit)}<div className="text-[11px] text-muted-foreground">{p.paper.exitDate ?? p.paper.status}</div></td>
                    <td className={cn('num px-2 py-1.5 font-medium', tone(p.real.netPct))}>{pct(p.real.netPct)}</td>
                    <td className={cn('num px-2 py-1.5 font-medium', tone(p.paper.netPct))}>{pct(p.paper.netPct)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {(r.offRule.length > 0 || r.missedReal.length > 0) && (
          <div className="grid gap-3 text-xs text-muted-foreground sm:grid-cols-2">
            {r.offRule.length > 0 && <p><span className="font-medium text-ink">Off the rules:</span> {r.offRule.map((t) => `${t.symbol} (${t.entryAt.slice(0, 10)})`).join(', ')}. These don't count toward the record.</p>}
            {r.missedReal.length > 0 && <p><span className="font-medium text-ink">Paper signals not traded for real:</span> {r.missedReal.join(', ')}.</p>}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
